import { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Alert,
  Avatar,
  Box,
  Button,
  IconButton,
  Menu,
  Snackbar,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import MicOffIcon from '@mui/icons-material/MicOff';
import { motion } from 'framer-motion';
import { MotionContainer, varBounceInDown } from '../../components/animate';
import { useWSContext } from '../../context/WebSocketContext';
import ConfirmationModal from '../../components/ConfirmationModal';

function ChatHead({
  userChat,
  nomeAtendente,
  isClientReconnecting,
  reconnectCountdown,
  chatId,
}) {
  const theme = useTheme();
  const {
    chatsEncerrados,
    confirmationModalData,
    handleConfirmarEncerramento,
    handleCancelarEncerramento,
    handleSendMessageWS,
    audioDisabledChats,
    marcarChatComoEncerrado,
    removeChat,
  } = useWSContext();

  const [abrir, setAbrir] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [reconnectAlert, setReconnectAlert] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [showAudioDisabledAlert, setShowAudioDisabledAlert] = useState(false);
  const audioNoticeShownByChatRef = useRef(new Set());

  const prevIsReconnecting = useRef(isClientReconnecting);
  const disconnectTimeRef = useRef(null);

  const ITEM_HEIGHT = 48;
  const open = Boolean(anchorEl);
  const nomeCliente = userChat?.state?.name || 'Cliente';
  const isChatEncerrado = chatsEncerrados.includes(chatId);
  const isAudioDisabledForClient = audioDisabledChats?.has?.(String(chatId));

  useEffect(() => {
    if (isClientReconnecting) {
      disconnectTimeRef.current = Date.now();
      setReconnectAlert({ open: false, message: '', severity: 'success' });
    }

    if (prevIsReconnecting.current && !isClientReconnecting) {
      const reconnectDuration = Date.now() - disconnectTimeRef.current;
      const message =
        reconnectDuration < 2000
          ? 'Conexão do cliente foi restaurada!'
          : 'Cliente reconectado com sucesso!';
      setReconnectAlert({ open: true, message, severity: 'success' });
      const timer = setTimeout(() => {
        setReconnectAlert({ open: false, message: '', severity: 'success' });
      }, 4000);
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [isClientReconnecting]);

  useEffect(() => {
    prevIsReconnecting.current = isClientReconnecting;
  }, [isClientReconnecting]);

  useEffect(() => {
    const chatKey = String(chatId || '');
    if (!isAudioDisabledForClient || !chatKey) {
      setShowAudioDisabledAlert(false);
      return undefined;
    }
    if (audioNoticeShownByChatRef.current.has(chatKey)) return undefined;

    audioNoticeShownByChatRef.current.add(chatKey);
    setShowAudioDisabledAlert(true);
    const timerId = setTimeout(() => setShowAudioDisabledAlert(false), 9000);
    return () => clearTimeout(timerId);
  }, [isAudioDisabledForClient, chatId]);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // Chama o backend para iniciar o fluxo de encerramento (vai abrir modal após resposta SYSTEM_MESSAGE)
  const handleIniciarEncerramento = () => {
    handleSendMessageWS({ serviceType: 'disconnectCliente', id: chatId });
    handleClose();
  };

  const handleConfirmAndCloseUI = (finalEquipmentName) => {
    handleConfirmarEncerramento(chatId, finalEquipmentName);
    marcarChatComoEncerrado(chatId);
    setTimeout(() => {
      removeChat(chatId);
    }, 4000);
  };

  const fecharSnack = (event, reason) => {
    if (reason === 'clickaway') return;
    setAbrir(false);
  };

  const copiarConversa = () => {
    const chatRaw = localStorage.getItem(userChat?.state?.id);
    if (chatRaw) {
      const chat = JSON.parse(chatRaw);
      const textoCopiado = chat
        .map((item) => {
          const nomeRemetente = (() => {
            const user = item.usuario?.toLowerCase?.();
            if (user === 'suporte') return nomeAtendente || 'Atendente';
            if (user === 'cliente') return nomeCliente;
            return item.usuario || nomeCliente;
          })();
          let conteudoMensagem;
          const caminhoDoArquivo = item.filePath || item.previewPath;
          const nomeArquivo = caminhoDoArquivo?.split('/').pop();
          if (item.mensagem?.trim()) {
            conteudoMensagem = item.mensagem;
          } else if (caminhoDoArquivo) {
            conteudoMensagem = nomeArquivo || '[Arquivo sem nome]';
            conteudoMensagem += `\nLink: ${caminhoDoArquivo}`;
          } else {
            return null;
          }
          return `${nomeRemetente} (${item.data || ''}):\n${conteudoMensagem}\n`;
        })
        .filter(Boolean)
        .join('\n');
      navigator.clipboard.writeText(textoCopiado);
      setAbrir(true);
    }
  };

  // Ao detectar wantClose do state, só chama a função de iniciar o encerramento (não abre modal manualmente)
  useEffect(() => {
    if (userChat?.state?.wantClose) {
      handleIniciarEncerramento();
    }
  }, [userChat, chatId]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          padding: 2,
          justifyContent: 'space-between',
          flexDirection: 'row',
          alignItems: 'center',
          background: 'rgba(255, 255, 255, 0.08)',
          boxShadow: theme.shadows[1],
        }}
      >
        <Box sx={{ flexDirection: 'row', display: 'flex', alignItems: 'center' }}>
          <Avatar
            alt={nomeCliente}
            src="/static/avatars/avatar_52.jpg"
            sx={{
              marginRight: 2,
              width: 56,
              height: 56,
              background: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            }}
          />
          <Box>
            <Typography variant="h6">{nomeCliente}</Typography>
            <Typography variant="body2">{`Nº de Série: ${
              userChat?.state?.serialNumber || '---'
            }`}</Typography>
          </Box>
        </Box>
        <Box>
          <IconButton
            id="long-button"
            onClick={handleClick}
            disabled={isChatEncerrado}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id="long-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{ style: { maxHeight: ITEM_HEIGHT * 4.5, width: '20ch' } }}
          >
            <Button fullWidth color="inherit" onClick={copiarConversa}>
              Copiar Conversa
            </Button>
            <Snackbar
              open={abrir}
              autoHideDuration={1000}
              onClose={fecharSnack}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              sx={{ ml: '120px', mb: '120px' }}
            >
              <Alert
                onClose={fecharSnack}
                variant="filled"
                severity="success"
                sx={{ width: '100%' }}
              >
                Texto Copiado!
              </Alert>
            </Snackbar>
            <Button
              fullWidth
              color="error"
              onClick={handleIniciarEncerramento}
              disabled={isChatEncerrado}
            >
              Encerrar Conversa
            </Button>
          </Menu>
        </Box>
      </Box>

      <MotionContainer initial="initial" open>
        <motion.div variants={varBounceInDown}>
          <Alert
            variant="filled"
            severity="error"
            sx={{
              display: isChatEncerrado ? 'flex' : 'none',
              borderRadius: '0px 0px 10px 10px',
              p: 1,
              justifyContent: 'center',
            }}
          >
            O Chat foi encerrado!
          </Alert>
        </motion.div>
        <motion.div variants={varBounceInDown}>
          <Alert
            variant="filled"
            severity="warning"
            sx={{
              display: isClientReconnecting && !isChatEncerrado ? 'flex' : 'none',
              borderRadius: '0px 0px 10px 10px',
              p: 1,
              justifyContent: 'center',
            }}
          >
            Cliente perdeu a conexão. Tentando reconectar... ({reconnectCountdown}s)
          </Alert>
        </motion.div>
        <motion.div variants={varBounceInDown}>
          <Alert
            variant="filled"
            severity={reconnectAlert.severity}
            sx={{
              display: reconnectAlert.open ? 'flex' : 'none',
              borderRadius: '0px 0px 10px 10px',
              p: 1,
              justifyContent: 'center',
            }}
          >
            {reconnectAlert.message}
          </Alert>
        </motion.div>
        <motion.div variants={varBounceInDown}>
          <Alert
            variant="filled"
            severity="info"
            icon={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <VolumeOffIcon fontSize="small" />
                <MicOffIcon fontSize="small" />
              </Box>
            )}
            sx={{
              display:
                isAudioDisabledForClient && showAudioDisabledAlert && !isChatEncerrado
                  ? 'flex'
                  : 'none',
              borderRadius: '0px 0px 10px 10px',
              p: 1,
              justifyContent: 'center',
            }}
          >
            Este cliente nao pode usar audio nem video neste aparelho (limitacao de hardware de midia). Envie texto, foto ou PDF.
          </Alert>
        </motion.div>
      </MotionContainer>

      <ConfirmationModal
        open={
          confirmationModalData?.isOpen && confirmationModalData?.chatId === chatId
        }
        data={confirmationModalData}
        onConfirm={handleConfirmAndCloseUI}
        onClose={handleCancelarEncerramento}
      />
    </Box>
  );
}

export default ChatHead;
